//---------------------------------------------------------------------------------------------------
// ## IMPORTS ##
// Library Imports
import { useEffect, useRef, useState } from "react";
import { Params, useNavigate, useParams } from "react-router-dom";
import { TextureLoader, Vector3 } from "three";
// Material ui Imports
// Custom components imports (and hooks and helpers)
import {
    VTBlurSphere,
    VTButton,
    VTCanvas,
    VTCreatorTimeline,
    VTCube,
    VTDialog,
    VTRichTextEditor,
    VTRichTextEditorContentPreview,
    VTSidePanel,
    VTTextField,
    VTTypography,
} from "@virtus-tech-repository/virtus-tech-repository";
import { useAppSelector } from "../../store/hooks";
import { IDragOptions } from "../../models/current.models";
import VTCreatorDropContainer, { DropPoint } from "../components/VTCreatorDropContainer";
import handleRenderHotspots from "../../helpers/canvasHotspotRenderer";
import { EMedia } from "@virtus-tech-repository/virtus-tech-repository/lib/models/media.model";
import {
    Box,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Paper,
    Slider,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { iImageProps } from "@virtus-tech-repository/virtus-tech-repository/lib/components/VTCube";
import { element } from "prop-types";
import {
    AddRounded,
    CancelRounded,
    DeleteRounded,
    EditRounded,
    SaveRounded,
    ArrowForwardRounded,
    Volcano,
    VolumeUpRounded,
    CloseRounded,
    VolumeOffRounded,
    PlayArrowRounded,
} from "@mui/icons-material";
import { EDragContentOptions } from "../../models/hotspotDrag.models";
import VTCreatorVideoControls from "../components/VTCreatorVideoControls";
import VTCreatorVideoTimeline from "../components/VTCreatorVideoTimeline";
import { IScenario, IScene } from "../../models/scenario.models";
import { EHotspotType, IHotspot, IUserInterface } from "../../models/hotspot.models";
import VTHotpsot from "../components/VTHotspot";
import { useDispatch } from "react-redux";
import { elementRoles } from "aria-query";
import {
    setCurrentAudio,
    setCurrentFeedbackImage,
    setCurrentFeedbackVideo,
    setCurrentHotspot,
    setCurrentQuestion,
    setCurrentScene,
    setCurrentSidePanel,
} from "../../store/slices/current.slice";
import VTHotspotQuickEdit from "../components/VTHotspotQuickEdit";
import ReactAudioPlayer from "react-audio-player";
import VTCreatorQuestion from "../components/VTCreatorQuestion";
import VTHotspotEditDialog from "../components/VTHotspotEditDialog";
import { Html } from "@react-three/drei";
import VTVideo from "../components/VTVideo";
import VTCreatorAudioControls from "../components/VTCreatorAudioControls";
import VTCreatorTimelineContainer from "./CreatorTimelineContainer";
import VTSphere from "../components/VTSphere";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import VTUserInterface from "../components/VTUserInterface";
import { VTHotspotHTMLWrapper } from "../components/VTHotspotHTMLWrapper";

import VRContainer from "../components/vr/VRContainer";
import VRContainerOne from "../components/vr/VRContainerOne";
import { setHotspotActions } from "../../store/slices/tracking.slice";
import * as animationData from "../../assets/lottie/dragmoveAnimation.json";
import Lottie from "lottie-react";

// Media imports

//---------------------------------------------------------------------------------------------------
// ## MUI STYLED ##
// Layout stuff remains inline, other styling pulled into it's own components

//---------------------------------------------------------------------------------------------------
// ## INTERFACES ##
// Optional variables towards the bottom
interface IProps {
    scenarioLoading: boolean;
    scene: IScene;
    handleMediaCanvasDropped: (timelineMedia: IDragOptions) => void;
    handleMediaQuestionCanvasDropped: (timelineMedia: IDragOptions, hotspotId?: string) => void;
    handleAddStopPoint: (sceneId: string, time: number, setVideoNextQuestion: any) => void;
    handleDeleteStopPoint: (sceneId: string, hotspotId: string) => void;
    isLoading: boolean;
    handleSaveHotspotLocation: (hotspotId: string, newPosition: Vector3, videoCurrentState: string | undefined) => void;
    handleDeleteHotspotLocation: (hotspotId: string, questionId?: string) => void;
    handleAudioDelete: (sceneId: string, questionId?: string) => void;
    handleEditQuestionTitle: (questionId: string, newTitle: string) => void;
    handleEditQuestionTimer: (questionId: string, newTimer: number) => void;
    scenario: IScenario;
    updateScenario: any;
    currentAttachedObject: any;
    setCurrentAttachedObject: any;
    handleMediaTimelineDropped: (timelineMedia: IDragOptions) => void;
    setSceneName: (sceneId: string, sceneName: string) => void;
    setSceneDelete: (sceneId: string) => void;
    setSceneDuplicate: (sceneId: string) => void;
    setSceneInitialView: (sceneId: string, newPosition: any) => void;
    setSceneStepper: (sceneId: string, value: boolean) => void;
    setSceneReorder: (index: number) => void;
    handleUpdateInterface: (hotspotId: string, newInterface: IHotspot) => void;
    videoCurrentState: "Paused" | "Playing" | "Question" | undefined;
    setVideoCurrentState: (videoCurrentState: "Paused" | "Playing" | "Question" | undefined) => void;
}

//---------------------------------------------------------------------------------------------------
// ## COMPONENTS ##
// Only display layouts in pages
export default function CreatorCanvasContainer({
    scenarioLoading,
    scene,
    handleMediaCanvasDropped,
    handleMediaQuestionCanvasDropped,
    handleAddStopPoint,
    handleDeleteStopPoint,
    handleSaveHotspotLocation,
    handleDeleteHotspotLocation,
    handleAudioDelete,
    handleEditQuestionTitle,
    handleEditQuestionTimer,
    isLoading,
    scenario,
    updateScenario,
    currentAttachedObject,
    setCurrentAttachedObject,
    handleMediaTimelineDropped,
    setSceneName,
    setSceneDelete,
    setSceneDuplicate,
    setSceneInitialView,
    setSceneStepper,
    setSceneReorder,
    handleUpdateInterface,
    videoCurrentState,
    setVideoCurrentState,
}: IProps) {
    //-------------------------------------------------------------------------------------------------
    // ## HOOKS ##
    const { scenarioId, sceneId }: Readonly<Params<string>> = useParams();
    const canvasControlsRef = useRef<any>();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    //-------------------------------------------------------------------------------------------------
    // ## USE SELECTOR ##

    //-------------------------------------------------------------------------------------------------
    // ## USE STATE ##
    const { src, hotspots, mediaType, id, startLocation } = scene;
    const [canvasHovered, setCanvasHovered] = useState<boolean>(false);

    // Image states
    const [currentImage, setCurrentImage] = useState<iImageProps | undefined>(undefined);

    // Video states

    const [video, setVideo] = useState<HTMLVideoElement>(document.createElement("video"));
    const [videoCurrentTime, setVideoCurrentTime] = useState<number>(0);
    const [videoLoadingState, setVideoLoadingState] = useState<boolean>(false);
    const [videoInitialLoad, setVideoInitialLoad] = useState<boolean>(false);

    const [videoNextQuestion, setVideoNextQuestion] = useState<IHotspot | undefined>(undefined);
    const [currentQuestions, setCurrentQuestions] = useState<IHotspot[]>([]);

    const [currentQuestionsTimes, setCurrentQuestionsTimes] = useState<number[]>([]);
    const [proceedVideoOnComplete, setProceedVideoOnComplete] = useState<boolean>(false);
    const [changeSceneOnComplete, setChangeSceneOnComplete] = useState<string | undefined>(undefined);

    const {
        currentlyDragging,
        currentHotspot,
        currentSidePanel,
        currentAudio,
        currentFeedbackImage,
        currentFeedbackVideo,
        currentFeedbackInput,
    } = useAppSelector((state) => state.currentReducer);

    const [bottomDrawWidth, setBottomDrawWidth] = useState<number>(0);
    const [timelineMode, setTimelineMode] = useState<"Stepper">("Stepper");

    const [currentInterface, setCurrentInterface] = useState<IHotspot | undefined>(undefined);

    const [showDragAnimation, setShowDragAnimation] = useState(false);
    const [drag360UserMsgShown, setDrag360UserMsgShown] = useState(false);

    useEffect(() => {
        if ((mediaType === EMedia.VIDEO_360 && videoCurrentState === "Playing") || mediaType === EMedia.IMAGE_360) {
            if (!drag360UserMsgShown) {
                setDrag360UserMsgShown(true);
                window.setTimeout(() => {
                    setShowDragAnimation(true);
                    window.setTimeout(() => {
                        setShowDragAnimation(false);
                    }, 7000);
                }, 250);
            }
        }
    }, [videoCurrentState, mediaType]);

    // clean-up when component unmounts
    useEffect(() => {
        return () => {
            dispatch(setCurrentScene({} as IScene));
        };
    }, []);

    //-------------------------------------------------------------------------------------------------
    // ## USE EFFECTS ##
    document.addEventListener("visibilitychange", function () {
        if (document.hidden && (mediaType === EMedia.VIDEO || mediaType === EMedia.VIDEO_360)) {
            if (!video.paused) {
                setVideoCurrentState("Paused");
                video.pause();
            }
        }
    });

    //-------------------------------------------------------------------------------------------------
    // Test data image loading and video question loading
    useEffect(() => {
        if (src && (mediaType === EMedia.VIDEO_360 || mediaType === EMedia.VIDEO) && hotspots) {
            getNextQuestion(0, hotspots, setVideoNextQuestion);
        }

        if (src && mediaType === EMedia.IMAGE_360) {
            const loader = new TextureLoader();
            const backTexture = loader.load(src + "/b.jpg");
            const frontTexture = loader.load(src + "/f.jpg");
            const leftTexture = loader.load(src + "/l.jpg");
            const rightTexture = loader.load(src + "/r.jpg");
            const upTexture = loader.load(src + "/u.jpg");
            const downTexture = loader.load(src + "/d.jpg");

            setCurrentImage({
                left: leftTexture,
                right: rightTexture,
                up: upTexture,
                down: downTexture,
                front: frontTexture,
                back: backTexture,
            });
        }
        setVideoCurrentState(undefined);
        setCurrentInterface(undefined);
    }, [src, scenarioId]);

    useEffect(() => {
        if (startLocation) {
            if (startLocation[0] !== 0 && startLocation[1] !== 0 && startLocation[2] !== 0) {
                // handleLookAt(0, 0, 0.9, false);

                handleLookAt(
                    parseFloat(startLocation[0]),
                    parseFloat(startLocation[1]),
                    parseFloat(startLocation[2]),
                    false,
                );
            }
        } else {
            handleLookAt(0, 0, 0.9, false);
        }
    }, [startLocation]);

    //-------------------------------------------------------------------------------------------------
    // Pause the video on question
    useEffect(() => {
        if (src && (mediaType === EMedia.VIDEO_360 || mediaType === EMedia.VIDEO)) {
            if (
                videoNextQuestion?.contents?.preClickContent.question?.time &&
                videoCurrentTime >= videoNextQuestion?.contents?.preClickContent.question?.time
            ) {
                dispatch(
                    setHotspotActions({
                        hotspotId: videoNextQuestion.id,
                        action: "Question",
                        date: new Date(),
                    }),
                );
                video.pause();
                if (videoNextQuestion.contents && videoNextQuestion.contents.preClickContent.question.audio) {
                    dispatch(setCurrentAudio(videoNextQuestion.contents.preClickContent.question.audio));
                }
                setVideoCurrentState("Question");
                dispatch(setCurrentQuestion(videoNextQuestion));
                dispatch(
                    setCurrentHotspot({
                        currentHotspot: undefined,
                        open: false,
                    }),
                );
            }
        }
    }, [videoCurrentTime]);

    useEffect(() => {
        if (src && (mediaType === EMedia.VIDEO_360 || mediaType === EMedia.VIDEO)) {
            getNextQuestion(videoCurrentTime - 0.5, hotspots, setVideoNextQuestion);
        }
    }, [hotspots]);

    useEffect(() => {
        if (
            video &&
            videoCurrentTime &&
            scene &&
            scene.hotspots &&
            setVideoNextQuestion &&
            !currentAudio &&
            !currentFeedbackImage &&
            !currentFeedbackVideo &&
            !currentFeedbackInput.hotspotId &&
            proceedVideoOnComplete
        ) {
            getNextQuestion(videoCurrentTime + 0.05, scene.hotspots, setVideoNextQuestion);
            setVideoCurrentState("Playing");
            video.play();
            setProceedVideoOnComplete(false);
        }
    }, [currentAudio, currentFeedbackImage, currentFeedbackVideo, currentFeedbackInput, proceedVideoOnComplete]);

    useEffect(() => {
        if (
            !currentAudio &&
            !currentFeedbackImage &&
            !currentFeedbackVideo &&
            !currentFeedbackInput.hotspotId &&
            changeSceneOnComplete !== undefined
        ) {
            setChangeSceneOnComplete(undefined);
            navigate(`/viewer/${scenarioId}/${changeSceneOnComplete}`);
        }
    }, [currentAudio, currentFeedbackImage, currentFeedbackVideo, currentFeedbackInput, changeSceneOnComplete]);

    //-------------------------------------------------------------------------------------------------
    // ## CUSTOM FUNCTIONS ##
    function getNextQuestion(
        time: number,
        hotspots: IHotspot[],
        setVideoNextQuestion: (nextQuestion: IHotspot | undefined) => void,
    ) {
        let nextQuestion: IHotspot | undefined;
        let localQuestions: IHotspot[] = [];
        let localQuestionsTimes: number[] = [];

        hotspots.forEach((element: IHotspot) => {
            if (element.type === EHotspotType.QUESTION) {
                localQuestions.push(element);
                if (element.contents?.preClickContent.question?.time) {
                    if (element.contents?.preClickContent.question?.time > time) {
                        if (
                            nextQuestion === undefined ||
                            nextQuestion.contents?.preClickContent.question?.time! >
                                element?.contents?.preClickContent.question?.time!
                        ) {
                            nextQuestion = element;
                        }
                    }
                }
            }
        });
        localQuestions.forEach((question: IHotspot) => {
            localQuestionsTimes.push(question.contents.preClickContent.question?.time!);
        });

        setCurrentQuestions(localQuestions);
        setCurrentQuestionsTimes(localQuestionsTimes);
        setVideoNextQuestion(nextQuestion);
    }

    function handleLookAt(x: number, y: number, z: number, transition?: boolean) {
        if (canvasControlsRef.current && startLocation) {
            canvasControlsRef.current.lookAt(x, y, z, transition);
        }
    }

    // const lottieOptions = {
    //     loop: true,
    //     autoplay: true,
    //     animationData: animationData,
    //     rendererSettings: {
    //         preserveAspectRatio: "xMidYMid slice",
    //     },
    // };

    const lottieContainerRef = useRef<any>(null!);

    //-------------------------------------------------------------------------------------------------
    // ## RETURN ##
    return (
        <div
            // ref={creatorCanvasContainerRef}
            id="creator-canvas-container"
            style={{
                backgroundColor: "#a9a9a9",
                height: "calc(100vh - " + 86 + "px)",
                width: "100%",
                marginTop: "86px",
                userSelect: "none",
                position: "relative",
            }}
            onMouseEnter={() => setCanvasHovered(true)}
            onMouseLeave={() => setCanvasHovered(false)}
            onMouseDown={() => {
                // setDrag360UserMsgShown(true);
            }}
        >
            {showDragAnimation && (
                <div
                    ref={lottieContainerRef}
                    style={{
                        pointerEvents: "none",
                        zIndex: 100000,
                        height: "220px",
                        width: "250px",
                        position: "absolute",
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "column",
                        left: "calc(50vw - 125px)",
                        top: "calc(50vh - 170px)",
                        userSelect: "none",
                        background: "#222",
                        borderRadius: "20px",
                        padding: " 0px 15px 15px 15px",
                        opacity: 0.8,
                    }}
                >
                    <div style={{ width: "150px", height: "150px" }}>
                        <Lottie animationData={animationData} loop={true} />
                    </div>
                    <Typography
                        variant="caption"
                        align="center"
                        sx={{ color: "#fff", fontWeight: "bold", fontSize: "1.1em" }}
                    >
                        {"Click And Drag To Look Around"}
                    </Typography>
                </div>
            )}
            {/*##############################################################################################*/}
            {/* Outside the canvas on image Hotspot */}
            {/*##############################################################################################*/}
            {/*---------------------------------------------------------------------------------------*/}
            {/* VT Drop container to accept media from the left side panel */}
            {videoCurrentState !== "Question" && (
                <>
                    {currentlyDragging &&
                        currentlyDragging.mediaType !== EDragContentOptions.SCENE &&
                        scenario.scenes.length > 0 && (
                            <VTCreatorDropContainer
                                handleMediaDropped={handleMediaCanvasDropped}
                                currentlyDragging={currentlyDragging}
                                location={DropPoint.DROP_CANVAS}
                                bottomDrawWidth={bottomDrawWidth}
                                videoNextQuestionID={videoNextQuestion && videoNextQuestion.id}
                            />
                        )}

                    {videoInitialLoad && (mediaType === EMedia.VIDEO_360 || mediaType === EMedia.VIDEO) && (
                        <PlayArrowRounded
                            sx={{
                                fontSize: "16em",
                                zIndex: 40000,
                                position: "absolute",
                                top: "calc(50% - 128px)",
                                left: "calc(50% - 128px)",
                                cursor: "pointer",
                                backgroundColor: "rgba(0,0,0,0.6)",
                                borderRadius: "50%",
                            }}
                            onClick={() => {
                                setVideoInitialLoad(false);
                                getNextQuestion(0, scene.hotspots, setVideoNextQuestion);
                                setVideoCurrentState("Playing");
                                video.play();
                            }}
                        />
                    )}

                    {scene.audio && (
                        <VTCreatorAudioControls
                            audio={scene.audio}
                            handleAudioDelete={handleAudioDelete}
                            questionId={undefined}
                        />
                    )}

                    {currentInterface ? (
                        <VTUserInterface
                            videoCurrentState={videoCurrentState}
                            currentInterface={currentInterface}
                            setCurrentInterface={setCurrentInterface}
                            handleDeleteHotspotLocation={handleDeleteHotspotLocation}
                            handleUpdateInterface={handleUpdateInterface}
                            handleProceedVideo={(delayInSeconds?: number) => {
                                if (currentHotspot.currentHotspot) {
                                    dispatch(
                                        setCurrentHotspot({
                                            currentHotspot: undefined,
                                            open: false,
                                        }),
                                    );
                                }
                                setTimeout(
                                    () => {
                                        if (
                                            currentAudio !== undefined ||
                                            currentFeedbackImage !== undefined ||
                                            currentFeedbackVideo !== undefined ||
                                            currentFeedbackInput.hotspotId !== undefined
                                        ) {
                                            setProceedVideoOnComplete(true);
                                        } else {
                                            getNextQuestion(
                                                videoCurrentTime + 0.05,
                                                scene.hotspots,
                                                setVideoNextQuestion,
                                            );
                                            setVideoCurrentState("Playing");
                                            video.play();
                                        }
                                    },
                                    delayInSeconds ? delayInSeconds * 1000 : 0,
                                );
                                // return () => clearTimeout(timer);
                            }}
                            handleChangeScene={(sceneId: string, delayInSeconds?: number) => {
                                setTimeout(
                                    () => {
                                        if (
                                            currentAudio !== undefined ||
                                            currentFeedbackImage !== undefined ||
                                            currentFeedbackVideo !== undefined ||
                                            currentFeedbackInput.hotspotId !== undefined
                                        ) {
                                            setChangeSceneOnComplete(sceneId);
                                        } else {
                                            navigate(`/viewer/${scenarioId}/${sceneId}`);
                                        }
                                    },
                                    delayInSeconds ? delayInSeconds * 1000 : 0,
                                );

                                // return () => clearTimeout(timer);
                            }}
                        />
                    ) : (
                        <Grid
                            sx={{
                                position: "absolute",
                                right: "50px",
                                width: 0,
                                height: "100%",
                            }}
                            container
                            alignContent={"center"}
                        >
                            {hotspots &&
                                hotspots.map(
                                    (hotspot: IHotspot) =>
                                        hotspot.type === EHotspotType.INTERFACE && (
                                            <Tooltip title={hotspot.contents.preClickContent.title}>
                                                <Grid item>
                                                    <VTButton
                                                        sx={{
                                                            zIndex: 10,
                                                            width: "10px",
                                                            height: "40px",
                                                            borderRadius: "15px 0px 0px 15px",
                                                            backgroundColor: "#a9a9a9",
                                                            ":hover": {
                                                                cursor: "pointer",
                                                                backgroundColor: "#888888",
                                                                boxShadow: "0 0 25px #2ad5c7",
                                                            },
                                                            marginBlock: "15px",
                                                        }}
                                                        onClick={() => setCurrentInterface(hotspot)}
                                                    >
                                                        <TextSnippetIcon />
                                                    </VTButton>
                                                </Grid>
                                            </Tooltip>
                                        ),
                                )}
                        </Grid>
                    )}
                </>
            )}

            {videoLoadingState && (
                <>
                    <CircularProgress sx={{ zIndex: 1000, position: "absolute", left: "48%", top: "48%" }} />
                </>
            )}

            {/*##############################################################################################*/}
            {/* Outside the canvas on Video  */}
            {/*##############################################################################################*/}
            {/*---------------------------------------------------------------------------------------*/}
            {/* On video add overlay video controls and question test */}
            {(mediaType === EMedia.VIDEO_360 || mediaType === EMedia.VIDEO) && (
                <>
                    {/*-----------------------------------------------------------------------------------*/}
                    {/* video controls */}
                    {canvasHovered && !videoInitialLoad && (
                        <VTCreatorVideoControls
                            videoCurrentTime={videoCurrentTime}
                            video={video}
                            videoNextQuestion={videoNextQuestion}
                            getNextQuestion={getNextQuestion}
                            setVideoNextQuestion={setVideoNextQuestion}
                            handleAddStopPoint={handleAddStopPoint}
                            scene={scene}
                            handleDeleteStopPoint={(sceneId: string, hotspotId: string) => {}}
                            videoCurrentState={videoCurrentState}
                            setVideoCurrentState={setVideoCurrentState}
                            currentInterfaceTrue={currentInterface !== undefined}
                            currentQuestionsTimes={currentQuestionsTimes}
                        />
                    )}

                    {/*---------------------------------------------------------------------------------*/}
                    {/* Question hotspot overlay */}
                    {videoNextQuestion && videoCurrentState === "Question" && (
                        <>
                            {/*---------------------------------------------------------------------------------*/}
                            {/* Question display */}
                            <VTCreatorQuestion
                                title={videoNextQuestion.contents?.preClickContent.title}
                                countdown={videoNextQuestion.contents?.preClickContent.question?.countdown}
                                questionId={videoNextQuestion.id}
                                handleEditQuestionTitle={handleEditQuestionTitle}
                                handleEditQuestionTimer={handleEditQuestionTimer}
                                marginTop={currentHotspot.currentHotspot ? "90px" : "5%"}
                            />

                            {/*---------------------------------------------------------------------------------*/}
                            {/* Question drop container */}
                            {currentlyDragging && currentlyDragging.mediaType !== EDragContentOptions.SCENE && (
                                <VTCreatorDropContainer
                                    handleMediaDropped={handleMediaQuestionCanvasDropped}
                                    currentlyDragging={currentlyDragging}
                                    location={DropPoint.DROP_CANVAS}
                                    bottomDrawWidth={bottomDrawWidth}
                                    videoNextQuestionID={videoNextQuestion && videoNextQuestion.id}
                                />
                            )}

                            {/*---------------------------------------------------------------------------------*/}
                            {/* Question audio controls */}
                            {videoNextQuestion.contents &&
                                videoNextQuestion.contents.preClickContent.question?.audio && (
                                    <VTCreatorAudioControls
                                        audio={videoNextQuestion.contents.preClickContent.question?.audio}
                                        handleAudioDelete={handleAudioDelete}
                                        questionId={videoNextQuestion.id}
                                    />
                                )}

                            {currentInterface ? (
                                <VTUserInterface
                                    videoCurrentState={videoCurrentState}
                                    currentInterface={currentInterface}
                                    setCurrentInterface={setCurrentInterface}
                                    handleDeleteHotspotLocation={handleDeleteHotspotLocation}
                                    handleUpdateInterface={handleUpdateInterface}
                                    handleProceedVideo={(delayInSeconds?: number) => {
                                        if (currentHotspot.currentHotspot) {
                                            dispatch(
                                                setCurrentHotspot({
                                                    currentHotspot: undefined,
                                                    open: false,
                                                }),
                                            );
                                        }
                                        setTimeout(
                                            () => {
                                                if (
                                                    currentAudio !== undefined ||
                                                    currentFeedbackImage !== undefined ||
                                                    currentFeedbackVideo !== undefined ||
                                                    currentFeedbackInput.hotspotId !== undefined
                                                ) {
                                                    setProceedVideoOnComplete(true);
                                                } else {
                                                    getNextQuestion(
                                                        videoCurrentTime + 0.05,
                                                        scene.hotspots,
                                                        setVideoNextQuestion,
                                                    );
                                                    setVideoCurrentState("Playing");
                                                    video.play();
                                                }
                                            },
                                            delayInSeconds ? delayInSeconds * 1000 : 0,
                                        );
                                        // return () => clearTimeout(timer);
                                    }}
                                    handleChangeScene={(sceneId: string, delayInSeconds?: number) => {
                                        setTimeout(
                                            () => {
                                                if (
                                                    currentAudio !== undefined ||
                                                    currentFeedbackImage !== undefined ||
                                                    currentFeedbackVideo !== undefined ||
                                                    currentFeedbackInput.hotspotId !== undefined
                                                ) {
                                                    setChangeSceneOnComplete(sceneId);
                                                } else {
                                                    navigate(`/viewer/${scenarioId}/${sceneId}`);
                                                }
                                            },
                                            delayInSeconds ? delayInSeconds * 1000 : 0,
                                        );

                                        // return () => clearTimeout(timer);
                                    }}
                                />
                            ) : (
                                <Grid
                                    sx={{
                                        position: "absolute",
                                        right: "50px",
                                        width: 0,
                                        height: "100%",
                                    }}
                                    container
                                    alignContent={"center"}
                                >
                                    {videoNextQuestion &&
                                        videoNextQuestion.contents &&
                                        videoNextQuestion.contents.preClickContent.question?.answers.map(
                                            (hotspot: IHotspot) =>
                                                hotspot.type === EHotspotType.INTERFACE && (
                                                    <Tooltip title={hotspot.contents.preClickContent.title}>
                                                        <Grid item>
                                                            <VTButton
                                                                sx={{
                                                                    zIndex: 10,
                                                                    width: "10px",
                                                                    height: "40px",
                                                                    borderRadius: "15px 0px 0px 15px",
                                                                    backgroundColor: "#a9a9a9",
                                                                    ":hover": {
                                                                        cursor: "pointer",
                                                                        backgroundColor: "#888888",
                                                                        boxShadow: "0 0 25px #2ad5c7",
                                                                    },
                                                                    marginBlock: "15px",
                                                                }}
                                                                onClick={() => setCurrentInterface(hotspot)}
                                                            >
                                                                <TextSnippetIcon />
                                                            </VTButton>
                                                        </Grid>
                                                    </Tooltip>
                                                ),
                                        )}
                                </Grid>
                            )}
                        </>
                    )}
                </>
            )}

            {scenarioId === "ab452c16-399f-4010-8907-9eea19c1fa01" && (
                <>
                    <VRContainer />
                </>
            )}

            {scenarioId === "0527fb1e-edad-459b-bb4f-dceaef8f743a" && (
                <>
                    <VRContainerOne />
                </>
            )}

            {scenarioId !== "ab452c16-399f-4010-8907-9eea19c1fa01" &&
                scenarioId !== "0527fb1e-edad-459b-bb4f-dceaef8f743a" && (
                    <>
                        {/*---------------------------------------------------------------------------------------*/}
                        {/* VT canvas */}

                        {mediaType !== EMedia.TEXT ? (
                            <VTCanvas
                                onDragging={{
                                    allowEventToBeCalled: showDragAnimation,
                                    callback: () => {
                                        setShowDragAnimation(false);
                                    },
                                }}
                                debug={false}
                                style={{
                                    width: "calc(100% - " + (currentInterface ? 400 : 0) + "px)",
                                    height: "100%",
                                    position: "absolute",
                                    top: 0,
                                }}
                                attachToCameraRef={currentAttachedObject}
                                cameraControlsRef={canvasControlsRef}
                            >
                                <>
                                    {/*-------------------------------------------------------------------------------------*/}
                                    {/* App image cube */}
                                    {mediaType === EMedia.IMAGE_360 && (
                                        <VTCube sideLength={500} loading={!src} image={currentImage} />
                                    )}

                                    {/*-------------------------------------------------------------------------------------*/}
                                    {/* Show default scene in none has been added */}
                                    {!scene.id && <VTCube sideLength={300} image={undefined} />}

                                    {/*-------------------------------------------------------------------------------------*/}
                                    {/* App video sphere */}
                                    {(mediaType === EMedia.VIDEO_360 || mediaType === EMedia.VIDEO) && (
                                        <>
                                            {mediaType === EMedia.VIDEO_360 && (
                                                <VTSphere
                                                    video={video}
                                                    setVideo={setVideo}
                                                    videoSrc={src}
                                                    setVideoCurrentTime={setVideoCurrentTime}
                                                    setVideoLoadingState={setVideoLoadingState}
                                                    setVideoInitialLoad={setVideoInitialLoad}
                                                />
                                            )}

                                            {mediaType === EMedia.VIDEO && (
                                                <VTVideo
                                                    video={video}
                                                    setVideo={setVideo}
                                                    videoSrc={src}
                                                    setVideoCurrentTime={setVideoCurrentTime}
                                                />
                                            )}

                                            {/*---------------------------------------------------------------------------------*/}
                                            {/* video paused */}
                                            {videoNextQuestion &&
                                                !videoInitialLoad &&
                                                videoCurrentState === "Question" &&
                                                videoNextQuestion.contents &&
                                                videoNextQuestion.contents.preClickContent.question!.answers.map(
                                                    (hotspot: IHotspot) => (
                                                        <>
                                                            {hotspot.type !== EHotspotType.INTERFACE &&
                                                                hotspot.type !== EHotspotType.QUESTION && (
                                                                    <VTHotspotHTMLWrapper
                                                                        key={hotspot.id}
                                                                        hotspot={hotspot}
                                                                        handleSaveHotspotLocation={
                                                                            handleSaveHotspotLocation
                                                                        }
                                                                        handleDeleteHotspotLocation={
                                                                            handleDeleteHotspotLocation
                                                                        }
                                                                        handleProceedVideo={(
                                                                            delayInSeconds?: number,
                                                                        ) => {
                                                                            if (currentHotspot.currentHotspot) {
                                                                                dispatch(
                                                                                    setCurrentHotspot({
                                                                                        currentHotspot: undefined,
                                                                                        open: false,
                                                                                    }),
                                                                                );
                                                                            }
                                                                            setTimeout(
                                                                                () => {
                                                                                    if (
                                                                                        currentAudio !== undefined ||
                                                                                        currentFeedbackImage !==
                                                                                            undefined ||
                                                                                        currentFeedbackVideo !==
                                                                                            undefined ||
                                                                                        currentFeedbackInput !==
                                                                                            undefined
                                                                                    ) {
                                                                                        setProceedVideoOnComplete(true);
                                                                                    } else {
                                                                                        getNextQuestion(
                                                                                            videoCurrentTime + 0.05,
                                                                                            scene.hotspots,
                                                                                            setVideoNextQuestion,
                                                                                        );
                                                                                        setVideoCurrentState("Playing");
                                                                                        video.play();
                                                                                    }
                                                                                },
                                                                                delayInSeconds
                                                                                    ? delayInSeconds * 1000
                                                                                    : 0,
                                                                            );
                                                                            // return () => clearTimeout(timer);
                                                                        }}
                                                                        handleChangeScene={(
                                                                            sceneId: string,
                                                                            delayInSeconds?: number,
                                                                        ) => {
                                                                            setTimeout(
                                                                                () => {
                                                                                    if (
                                                                                        currentAudio !== undefined ||
                                                                                        currentFeedbackImage !==
                                                                                            undefined ||
                                                                                        currentFeedbackVideo !==
                                                                                            undefined ||
                                                                                        currentFeedbackInput !==
                                                                                            undefined
                                                                                    ) {
                                                                                        setChangeSceneOnComplete(
                                                                                            sceneId,
                                                                                        );
                                                                                    } else {
                                                                                        navigate(
                                                                                            `/viewer/${scenarioId}/${sceneId}`,
                                                                                        );
                                                                                    }
                                                                                },
                                                                                delayInSeconds
                                                                                    ? delayInSeconds * 1000
                                                                                    : 0,
                                                                            );

                                                                            // return () => clearTimeout(timer);
                                                                        }}
                                                                        setCurrentAttachedObject={
                                                                            setCurrentAttachedObject
                                                                        }
                                                                        currentAttachedObject={currentAttachedObject}
                                                                        videoCurrentState={videoCurrentState}
                                                                    />
                                                                )}
                                                        </>
                                                    ),
                                                )}
                                        </>
                                    )}

                                    {/*-------------------------------------------------------------------------------------*/}
                                    {/* Render hotspots */}
                                    {hotspots &&
                                        !videoInitialLoad &&
                                        (videoCurrentState !== "Question" ||
                                            (mediaType !== EMedia.VIDEO_360 && mediaType !== EMedia.VIDEO)) &&
                                        hotspots.map((hotspot: IHotspot, i: number) => (
                                            <>
                                                {hotspot.type !== EHotspotType.QUESTION &&
                                                    hotspot.type !== EHotspotType.INTERFACE && (
                                                        <VTHotspotHTMLWrapper
                                                            key={hotspot.id}
                                                            hotspot={hotspot}
                                                            handleSaveHotspotLocation={handleSaveHotspotLocation}
                                                            handleDeleteHotspotLocation={handleDeleteHotspotLocation}
                                                            currentAttachedObject={currentAttachedObject}
                                                            setCurrentAttachedObject={setCurrentAttachedObject}
                                                            videoCurrentState={videoCurrentState}
                                                            handleChangeScene={(
                                                                sceneId: string,
                                                                delayInSeconds?: number,
                                                            ) => {
                                                                setTimeout(
                                                                    () => {
                                                                        if (
                                                                            currentAudio !== undefined ||
                                                                            currentFeedbackImage !== undefined ||
                                                                            currentFeedbackVideo !== undefined ||
                                                                            currentFeedbackInput !== undefined
                                                                        ) {
                                                                            setChangeSceneOnComplete(sceneId);
                                                                        } else {
                                                                            navigate(
                                                                                `/viewer/${scenarioId}/${sceneId}`,
                                                                            );
                                                                        }
                                                                    },
                                                                    delayInSeconds ? delayInSeconds * 1000 : 0,
                                                                );

                                                                // return () => clearTimeout(timer);
                                                            }}
                                                        />
                                                    )}
                                            </>
                                        ))}
                                </>
                            </VTCanvas>
                        ) : (
                            <Container>
                                <Grid
                                    sx={{
                                        // width: "100%",
                                        height: "100vh",
                                        // position: "absolute",
                                        // top: 0,
                                        background: "#393939",
                                    }}
                                >
                                    <VTRichTextEditorContentPreview htmlContent={scene.src} />
                                </Grid>
                            </Container>
                        )}
                    </>
                )}

            <VTCreatorTimelineContainer
                scenario={scenario}
                scenarioLoading={scenarioLoading}
                handleMediaTimelineDropped={handleMediaTimelineDropped}
                setSceneName={setSceneName}
                setSceneDelete={setSceneDelete}
                setSceneInitialView={setSceneInitialView}
                setSceneStepper={setSceneStepper}
                setSceneReorder={setSceneReorder}
                isLoading={isLoading}
                bottomDrawWidth={bottomDrawWidth}
                setBottomDrawWidth={setBottomDrawWidth}
                timelineMode={timelineMode}
                setTimelineMode={setTimelineMode}
                mediaType={mediaType}
                videoNextQuestion={videoNextQuestion}
                videoCurrentState={videoCurrentState}
                setVideoCurrentState={setVideoCurrentState}
                currentQuestions={currentQuestions}
                videoCurrentTime={videoCurrentTime}
                video={video}
                getNextQuestion={getNextQuestion}
                setVideoNextQuestion={setVideoNextQuestion}
                setSceneDuplicate={setSceneDuplicate}
                currentInterface={currentInterface}
            />

            {currentHotspot.currentHotspot && (
                <VTHotspotQuickEdit
                    scenario={scenario}
                    updateScenario={updateScenario}
                    videoCurrentState={videoCurrentState}
                />
            )}

            <VTHotspotEditDialog
                sx={{
                    "& .MuiDialogContent-root": {
                        height: "1000px", // make dialog a fixed height
                    },
                    position: "absolute",
                    left: currentSidePanel.width + 100,
                    height: "90vh",
                    marginTop: "80px",
                }}
                scenario={scenario}
                updateScenario={updateScenario}
                videoNextQuestion={videoNextQuestion}
                videoCurrentTime={videoCurrentTime}
                videoCurrentState={videoCurrentState}
            />
        </div>
    );
}
